import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import { Text } from "@mantine/core";
import {
  ItemMenu,
  ItemMenuData,
  MultiSelectAction,
} from "components/UI/ItemMenu/ItemMenu";
import { useExportGPX } from "hooks/useExportGPX";
import { useGenericModals } from "hooks/useGenericModals";
import { useUserWaypoints } from "hooks/useUserWaypoints";
import { useTranslation } from "react-i18next";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { formatCoords } from "utils/formatters/formatCoords";
import { filterDataByString } from "utils/helpers/filterData";
import { getWaypointIconSrc } from "utils/helpers/icons/getWaypointIconName";
import { getSortingOptions, sortItems } from "utils/sorting/itemSorting";
import { FaLocationDotPlus } from "assets/icons/FaLocationDotPlus";
import { SortByPicker } from "components/UI/SortByPicker";
import TracksWaypointsMenuPlaceholder from "./TracksWaypointsMenuPlaceholder";

const SORT_BYS = ["name", "createdDate"] as const;

export const WaypointsMenu = () => {
  const {
    waypoints,
    hiddenWaypointIds,
    selectWaypoint,
    selectedWaypoint,
    isFetching,
    deleteWaypoints,
    toggleWaypointVisibility,
  } = useUserWaypoints();
  const {
    dispatch,
    state: { isLeftMenuOpen, sortWaypoints },
    updateSaveable,
  } = useOptions();
  const [filterKeyword, setFilterKeyword] = useState("");
  const selectedWaypointRef = useRef(selectedWaypoint);
  const { openConfirmWaypointDeleteModal } = useGenericModals();
  const { t } = useTranslation();
  const waypointsRef = useRef(waypoints);

  useEffect(() => {
    waypointsRef.current = waypoints;
  }, [waypoints]);

  useEffect(() => {
    selectedWaypointRef.current = selectedWaypoint;
  }, [selectedWaypoint]);

  const isSmallViewport = useIsSmallViewport();

  const handleMenuItemSelect = useCallback(
    (id: number) => {
      if (id !== selectedWaypointRef.current?.id) {
        if (isLeftMenuOpen && isSmallViewport) {
          dispatch(updateNonSaveableOptions({ isLeftMenuOpen: false }));
        }
        selectWaypoint(id, { focus: true });
      } else {
        selectWaypoint(null);
      }
    },
    [dispatch, isLeftMenuOpen, isSmallViewport, selectWaypoint]
  );

  const exportGPX = useExportGPX();
  const exportToGPX: MultiSelectAction = useCallback(
    (ids, done) => {
      const items = waypointsRef.current?.filter(({ id }) => ids.includes(id));
      if (items && exportGPX({ waypoints: items })) done();
    },
    [exportGPX]
  );

  const deleteItems: MultiSelectAction = useCallback(
    (ids, callback) => {
      openConfirmWaypointDeleteModal({
        count: ids.length,
        onConfirm: () => {
          void deleteWaypoints(ids).then(callback);
        },
      });
    },
    [deleteWaypoints, openConfirmWaypointDeleteModal]
  );

  const handleMenuItemRightButtonClick = useCallback(
    (id: number) => {
      toggleWaypointVisibility(id);
    },
    [toggleWaypointVisibility]
  );

  const data = waypoints?.map((waypoint) => {
    const itemData: ItemMenuData = {
      ...waypoint,
      bottomInfo: formatCoords(waypoint.point),
      leftImageSrc: getWaypointIconSrc(waypoint.icon || "Default"),
    };
    return itemData;
  });

  const filteredData = filterDataByString(data, "name", filterKeyword);

  const itemData = sortItems(
    filteredData,
    sortWaypoints.type,
    sortWaypoints.direction === "asc"
  );

  const CurrentSortByPicker = useMemo(
    () => (
      <SortByPicker
        data={getSortingOptions(t, SORT_BYS)}
        value={sortWaypoints.type}
        sortDirection={sortWaypoints.direction}
        onItemChange={(value, direction) => {
          updateSaveable({ sortWaypoints: { type: value, direction } });
        }}
      />
    ),
    [sortWaypoints.direction, sortWaypoints.type, t, updateSaveable]
  );

  const handleMultiSelectModeOn = useCallback(() => {
    selectWaypoint(null);
  }, [selectWaypoint]);

  return useMemo(
    () => (
      <ItemMenu
        data={itemData}
        selectedItemId={selectedWaypoint?.id}
        hiddenItemIds={hiddenWaypointIds}
        onItemClick={handleMenuItemSelect}
        onItemRightButtonClick={handleMenuItemRightButtonClick}
        loading={isFetching}
        placeholder={
          <TracksWaypointsMenuPlaceholder
            emptyText={
              <Text
                p="md"
                c="dimmed"
                size="sm"
                style={{ textAlign: "center", userSelect: "none" }}
              >
                {t("waypoints_menu.help_text_01")}
                {t("waypoints_menu.help_text_02a")}{" "}
                <FaLocationDotPlus color="#7950f2" />{" "}
                {t("waypoints_menu.help_text_02b")}
                {t("waypoints_menu.help_text_03")}
                {t("waypoints_menu.help_text_04")}
              </Text>
            }
          />
        }
        onExportToGPXclick={exportToGPX}
        onDeleteClick={deleteItems}
        onMultiSelectModeOn={handleMultiSelectModeOn}
        onFilterInput={setFilterKeyword}
        topSubmenuComponent={CurrentSortByPicker}
      />
    ),
    [
      t,
      CurrentSortByPicker,
      deleteItems,
      exportToGPX,
      handleMenuItemRightButtonClick,
      handleMenuItemSelect,
      handleMultiSelectModeOn,
      isFetching,
      itemData,
      selectedWaypoint?.id,
      hiddenWaypointIds,
    ]
  );
};
